import { Metadata } from '@redwoodjs/web';
import {
  RiAttachment2,
  RiBriefcaseFill,
  RiFileList3Fill,
  RiGroupFill,
  RiMailFill,
  RiMoneyDollarCircleLine,
  RiRobot2Fill,
  RiStore3Fill,
} from '@remixicon/react';
import { useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';
import { PageBody } from 'src/components/containers/PageBody';
import { PageHeader, PageType } from 'src/components/containers/PageHeader';
import { buildAppHref } from 'src/lib/routing';
import { appLinks } from 'src/routing/appLinks';
import SettingsCard, { SettingsCardProps } from './SettingsCard';

type SettingsLink = SettingsCardProps & { hide?: boolean };

export default function SettingsPage() {
  const router = useRouter();
  const pages = useMemo<SettingsLink[]>(
    () => [
      {
        title: 'Accounting',
        description: 'Manage accounts, payments, and other financial information',
        icon: RiMoneyDollarCircleLine,
        route: buildAppHref(router, appLinks.settingsAccounting()),
      },
      {
        title: 'Business Setup',
        description: 'Add business entities, contacts, set charges and prices',
        icon: RiBriefcaseFill,
        route: buildAppHref(router, appLinks.settingsBusiness()),
      },
      {
        title: 'Automations',
        description: 'Set conditions and triggers to build faster workflows',
        icon: RiRobot2Fill,
        route: buildAppHref(router, appLinks.settingsAutomations()),
        hide: true,
      },
      {
        title: 'Email Management',
        description: 'Manage email templates for various send outs',
        icon: RiMailFill,
        route: buildAppHref(router, appLinks.settingsEmails()),
        hide: true,
      },
      {
        title: 'Order Management',
        description: 'Configure specific details for all types of orders',
        icon: RiFileList3Fill,
        route: buildAppHref(router, appLinks.settingsOrders()),
        hide: true,
      },
      {
        title: 'Documents',
        description: 'Edit templates, add custom data and manage types of documents',
        icon: RiAttachment2,
        route: buildAppHref(router, appLinks.settingsDocuments()),
        hide: true,
      },
      {
        title: 'Inventory Management',
        description: 'Manage products, warehouses and PR settings',
        icon: RiStore3Fill,
        route: buildAppHref(router, appLinks.settingsInventory()),
      },
      {
        title: 'Team',
        description: 'Manage organisation members and configure and permissions',
        icon: RiGroupFill,
        route: buildAppHref(router, appLinks.settingsTeam()),
      },
    ],
    [router]
  );

  return (
    <>
      <Metadata title="Settings" />
      <PageHeader pageType={PageType.Settings} />
      <PageBody>
        <div className="grid w-full max-w-[1322px] grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {pages
            .filter((page) => !page.hide)
            .map(({ hide, ...cardProps }) => (
              <SettingsCard key={cardProps.title} {...cardProps} />
            ))}
        </div>
      </PageBody>
    </>
  );
}
